import React from 'react';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import { Modal } from '@ui/Modal';
import { Button } from '@ui/Button/beta';
import palette from '@ui/palette';
import ErrorMessage from '@ui/ErrorMessage';
import styles from './StartModal.module.css';

export type StartModalProps = {
  id?: string;
  'data-e2e'?: string;
  open?: boolean;
  close: () => void;
  onImport: () => void;
  onStartBlank: () => void;
  error?: string;
};

/**
 * Starting point for guided project creation — choose between importing
 * a script or starting with a blank project. Built from the Figma
 * "Guided project creation" start modal.
 */
function StartModal(props: StartModalProps) {
  const {
    id = 'start-modal',
    'data-e2e': dataE2E,
    open,
    close,
    onImport,
    onStartBlank,
    error,
  } = props;

  return (
    <Modal
      id={id}
      data-e2e={dataE2E ?? id}
      open={open}
      close={close}
      title={null}
    >
      <div className={styles.container}>
        <Button
          variant="text"
          className={styles['close-button']}
          onClick={close}
          aria-label="Close"
        >
          <Icon type="close" fontSize="large" htmlColor="var(--text-primary)" />
        </Button>

        <div className={styles.cards}>
          <button
            type="button"
            className={styles.card}
            aria-label="Import and go - bring your script and we'll structure it"
            onClick={() => {
              onImport();
            }}
          >
            <div className={styles['card-content']}>
              <div className={styles['icon-section']}>
                <Icon
                  type="stars03"
                  fontSize="large"
                  htmlColor={palette.lavender_300}
                />
              </div>
              <div className={styles['text-section']}>
                <Typography
                  variant="h3"
                  component="h2"
                  className={styles.title}
                  bold
                >
                  Import & go
                </Typography>
                <Typography
                  variant="pMd"
                  component="p"
                  color="secondary"
                  className={styles.description}
                >
                  Bring your script in. We&apos;ll
                  <br /> structure it, split it, and cast <br /> the voices.
                </Typography>
              </div>
            </div>
            <div className={styles['button-wrapper']}>
              <span className={styles['fast-path']}>Fast path</span>
              <Icon
                type="chevronRight"
                fontSize="small"
                htmlColor={palette.lavender_300}
              />
            </div>
          </button>

          <button
            type="button"
            className={styles.card}
            aria-label="Start blank project - open an empty project ready to paste your script"
            onClick={() => {
              onStartBlank();
            }}
          >
            <div className={styles['card-content']}>
              <div className={styles['icon-section']}>
                <Icon
                  type="pencil02"
                  fontSize="large"
                  htmlColor="var(--text-primary)"
                />
              </div>
              <div className={styles['text-section']}>
                <Typography
                  variant="h3"
                  component="h2"
                  className={styles.title}
                  bold
                >
                  Start blank project
                </Typography>
                <Typography
                  variant="pMd"
                  component="p"
                  color="secondary"
                  className={styles.description}
                >
                  Open an empty project, ready to paste or import your script
                  into.
                </Typography>
              </div>
            </div>
            <div className={styles['button-wrapper']}>
              <span className={styles['button-text']}>Open now</span>
              <Icon type="chevronRight" fontSize="small" />
            </div>
          </button>
        </div>

        {error && <ErrorMessage type="error">{error}</ErrorMessage>}
      </div>
    </Modal>
  );
}

export default StartModal;
